import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, Eye, EyeOff, Image, Upload, ArrowUp, ArrowDown } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const HappyCustomersManagement = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [formData, setFormData] = useState({
    image_url: '',
    caption: '',
    customer_name: '',
    priority: 0,
    is_visible: true
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${API}/admin/happy-customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPhotos(res.data);
    } catch (error) {
      toast.error('Failed to fetch photos');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(`${API}/admin/upload-image`, uploadData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData(prev => ({ ...prev, image_url: res.data.url }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.image_url) {
      toast.error('Please upload an image');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      if (editingPhoto) {
        await axios.patch(`${API}/admin/happy-customers/${editingPhoto.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Photo updated');
      } else {
        await axios.post(`${API}/admin/happy-customers`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Photo added');
      }
      setShowDialog(false);
      setEditingPhoto(null);
      setFormData({ image_url: '', caption: '', customer_name: '', priority: 0, is_visible: true });
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to save photo');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/happy-customers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Photo deleted');
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const toggleVisibility = async (photo) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(`${API}/admin/happy-customers/${photo.id}`, {
        is_visible: !photo.is_visible
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Photo ${photo.is_visible ? 'hidden' : 'visible'}`);
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  const updatePriority = async (photo, delta) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(`${API}/admin/happy-customers/${photo.id}`, {
        priority: photo.priority + delta
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to update priority');
    }
  };

  const openEditDialog = (photo) => {
    setEditingPhoto(photo);
    setFormData({
      image_url: photo.image_url,
      caption: photo.caption || '',
      customer_name: photo.customer_name || '',
      priority: photo.priority || 0,
      is_visible: photo.is_visible
    });
    setShowDialog(true);
  };

  const openAddDialog = () => {
    setEditingPhoto(null);
    setFormData({ image_url: '', caption: '', customer_name: '', priority: 0, is_visible: true });
    setShowDialog(true);
  };

  if (loading) {
    return <div className="text-center py-8 text-[#a8b8d0]">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Happy Customers Gallery</h2>
          <p className="text-[#a8b8d0] mt-1">Photos displayed on the homepage in random order. Higher priority = more likely to appear first.</p>
        </div>
        <button
          onClick={openAddDialog}
          className="flex items-center gap-2 bg-[#ff6b35] hover:bg-[#e55a2b] text-white px-4 py-2 rounded-lg"
        >
          <Plus size={20} /> Add Photo
        </button>
      </div>

      {/* Photos Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {photos.map(photo => (
          <div key={photo.id} className={`bg-[#132238] rounded-xl overflow-hidden border ${photo.is_visible ? 'border-[#1a2d47]' : 'border-red-500/50 opacity-60'}`}>
            <div className="aspect-square relative">
              <img src={photo.image_url} alt={photo.caption || 'Happy customer'} className="w-full h-full object-cover" />
              <div className="absolute top-2 right-2 flex gap-1">
                <button
                  onClick={() => toggleVisibility(photo)}
                  className={`p-1.5 rounded ${photo.is_visible ? 'bg-green-500/80' : 'bg-red-500/80'} text-white`}
                  title={photo.is_visible ? 'Visible - Click to hide' : 'Hidden - Click to show'}
                >
                  {photo.is_visible ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
              </div>
              <div className="absolute bottom-2 left-2 bg-black/60 px-2 py-1 rounded text-white text-xs">
                Priority: {photo.priority}
              </div>
            </div>
            <div className="p-3">
              {photo.customer_name && (
                <p className="text-white font-medium truncate">{photo.customer_name}</p>
              )}
              {photo.caption && (
                <p className="text-[#a8b8d0] text-sm truncate">{photo.caption}</p>
              )}
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => updatePriority(photo, 1)}
                  className="flex-1 bg-[#1a2d47] hover:bg-[#1a4d8c] text-white p-2 rounded text-sm"
                  title="Increase priority"
                >
                  <ArrowUp size={16} className="mx-auto" />
                </button>
                <button
                  onClick={() => updatePriority(photo, -1)}
                  className="flex-1 bg-[#1a2d47] hover:bg-[#1a4d8c] text-white p-2 rounded text-sm"
                  title="Decrease priority"
                >
                  <ArrowDown size={16} className="mx-auto" />
                </button>
                <button
                  onClick={() => openEditDialog(photo)}
                  className="flex-1 bg-[#1a2d47] hover:bg-[#1a4d8c] text-white p-2 rounded text-sm"
                >
                  <Edit2 size={16} className="mx-auto" />
                </button>
                <button
                  onClick={() => handleDelete(photo.id)}
                  className="flex-1 bg-red-500/20 hover:bg-red-500/40 text-red-400 p-2 rounded text-sm"
                >
                  <Trash2 size={16} className="mx-auto" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {photos.length === 0 && (
        <div className="text-center py-12 bg-[#132238] rounded-xl border border-[#1a2d47]">
          <Image size={48} className="mx-auto text-[#64748b] mb-4" />
          <p className="text-[#a8b8d0]">No happy customer photos yet.</p>
          <p className="text-[#64748b] text-sm mt-1">Add photos to display them on the homepage.</p>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md bg-[#132238] border-[#1a2d47] text-white">
          <DialogHeader>
            <DialogTitle>{editingPhoto ? 'Edit Photo' : 'Add Happy Customer Photo'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Image Preview/Upload */}
            <div>
              <label className="text-[#a8b8d0] text-sm block mb-2">Photo *</label>
              {formData.image_url ? (
                <div className="relative">
                  <img src={formData.image_url} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-48 bg-[#0a1628] border-2 border-dashed border-[#1a2d47] rounded-lg cursor-pointer hover:border-[#ff6b35]">
                  <Upload size={32} className="text-[#64748b] mb-2" />
                  <span className="text-[#a8b8d0]">
                    {uploading ? 'Uploading...' : 'Click to upload photo'}
                  </span>
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                </label>
              )}
            </div>

            {/* Customer Name */}
            <div>
              <label className="text-[#a8b8d0] text-sm block mb-2">Customer Name (optional)</label>
              <input
                type="text"
                value={formData.customer_name}
                onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                placeholder="John D."
                className="w-full bg-[#0a1628] border border-[#1a2d47] rounded-lg px-4 py-2 text-white"
              />
            </div>

            {/* Caption */}
            <div>
              <label className="text-[#a8b8d0] text-sm block mb-2">Caption (optional)</label>
              <input
                type="text"
                value={formData.caption}
                onChange={(e) => setFormData(prev => ({ ...prev, caption: e.target.value }))}
                placeholder="Happy with their new Toyota!"
                className="w-full bg-[#0a1628] border border-[#1a2d47] rounded-lg px-4 py-2 text-white"
              />
            </div>

            {/* Priority */}
            <div>
              <label className="text-[#a8b8d0] text-sm block mb-2">Priority (higher = shown first)</label>
              <input
                type="number"
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                className="w-full bg-[#0a1628] border border-[#1a2d47] rounded-lg px-4 py-2 text-white"
              />
            </div>

            {/* Visibility */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_visible}
                onChange={(e) => setFormData(prev => ({ ...prev, is_visible: e.target.checked }))}
                className="w-4 h-4"
              />
              <span className="text-[#a8b8d0]">Visible on website</span>
            </label>

            <button
              type="submit"
              className="w-full bg-[#ff6b35] hover:bg-[#e55a2b] text-white font-bold py-3 rounded-lg"
            >
              {editingPhoto ? 'Update Photo' : 'Add Photo'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HappyCustomersManagement;
